/**
 * The log of a live connection (WebSocket, Server-Sent Events) — pure helpers for
 * `ui/StreamLogTable`: what a row is, how rows are filtered, how a payload is shown.
 *
 * An entry: { id, kind: 'message' | 'event', timestamp, data,
 *             direction?: 'in' | 'out',      // messages
 *             tag?: string,                  // SSE: the event's name ("stock", "hello")
 *             event?: 'connected' | 'disconnected' | 'error', detail?: object }  // lifecycle rows
 */
import { formatJson } from './formatCode';

export const LOG_FILTERS = Object.freeze(['all', 'received', 'sent', 'events']);

export function filterEntries(entries, { filter = 'all', query = '' } = {}) {
  const q = String(query || '').trim().toLowerCase();
  return (entries || []).filter((e) => {
    if (filter === 'received' && !(e.kind === 'message' && e.direction === 'in')) return false;
    if (filter === 'sent' && !(e.kind === 'message' && e.direction === 'out')) return false;
    if (filter === 'events' && e.kind !== 'event') return false;
    if (!q) return true;
    return `${e.data ?? ''} ${e.tag ?? ''}`.toLowerCase().includes(q);
  });
}

/** Which highlighter fits a payload: json | xml | html | text. */
export function detectPayloadLanguage(text) {
  const s = String(text ?? '').trim();
  if (!s) return 'text';
  if (/^[{[]/.test(s)) { try { JSON.parse(s); return 'json'; } catch { /* looks like JSON, is not */ } }
  if (/^<!doctype html|^<html[\s>]/i.test(s)) return 'html';
  if (/^<\?xml/.test(s)) return 'xml'; // says so itself
  // a fragment: HTML only when it uses tags that mean nothing else (<a>, <p> are XML names too)
  if (/^<[a-zA-Z][\w:.-]*[\s>/]/.test(s)) return /<(html|head|body|div|span|table|ul|ol|li|h[1-6]|img|br)\b/i.test(s) ? 'html' : 'xml';
  return 'text';
}

/** The payload as the preview shows it: JSON pretty-printed, everything else as it came. */
export function formatPayload(text, language) {
  if (language !== 'json') return String(text ?? '');
  try { return formatJson(text); } catch { return String(text ?? ''); } // digits above 2^53 survive
}

/** HH:mm:ss.SSS — the wall clock a person compares with a server log. */
export function formatLogTime(ms) {
  const d = new Date(ms);
  const p = (n, w = 2) => String(n).padStart(w, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`;
}

/** One line for the row: whitespace collapsed (a pretty-printed payload must not grow the row). */
export const oneLine = (text) => String(text ?? '').replace(/\s+/g, ' ').trim();
